// Apple-ecosystem visual pass (2026-09-20) — LOOK ONLY.
// This file is appended to the end of the main stylesheet in PoolDesk.jsx, so
// every rule here wins by coming last. It changes colours, corners, type,
// spacing and shadows. It never changes layout mechanics the app depends on:
//   - html zoom stays 1.08 (owner's rule 08/03)
//   - the schedule holding bin / truck board rules are NOT restated here
//     (holdingBinStickyContract + verticalTilesContract read the first match)
//   - red STOP / unpaid / flag alerts keep their colour and their flashing
// Checked against Apple's Human Interface Guidelines (developer.apple.com/design/
// human-interface-guidelines — Typography, Accessibility, Color, Layout):
//   - text contrast 4.5:1 or better for everything restyled here (measured)
//   - phones: 44pt default touch target, 28pt minimum (values below are before
//     the 1.08 zoom, so 41px lands at 44 and 26px at 28)
//   - text no smaller than 11pt on phones, 10pt on desktop, after zoom
//   - no Light/Thin weights; hierarchy from size + weight, not ALL CAPS
//   - Reduce Motion: movement swapped for plain fades
//   - Increase Contrast: darker text, lines and accent
// To roll the whole look back: remove "+ APPLE_CSS" where CSS is defined.

export const APPLE_TOKENS = {
  accent: "#0071E3",       // Apple blue (the apple.com button blue) — white text 4.7:1
  accentHover: "#0062C4",  // hover/press goes darker — white text 5.9:1
  accentDeep: "#0058B0",   // accent used as TEXT on tinted fills — 5.2:1 or better
  ink: "#1D1D1F",          // Apple primary label. Brand navy lives on in the logo and gate card
  page: "#F2F2F7",         // iOS systemGroupedBackground
  card: "#FFFFFF",
  fill: "rgba(118,118,128,.12)",   // iOS tertiary fill — segmented tracks, hovers
  fillSoft: "#F5F5F7",             // quiet inset surface (apple.com grey)
  line: "#E0E0E6",         // hairline separator
  muted: "#66666C",        // secondary label — 5.0:1 or better on every surface used
  radiusS: "8px", radiusM: "11px", radiusL: "16px", radiusXL: "22px",
};

const T = APPLE_TOKENS;

export const APPLE_CSS = `
/* ───────────── 1. TOKENS ───────────── */
div.pd {
  --ink: ${T.ink}; --brand: ${T.accent}; --navy: #17356E;
  --tile: ${T.accent}; --tile-lit: ${T.accentHover}; --accent-deep: ${T.accentDeep};
  --plaster: ${T.page}; --card: ${T.card}; --line: ${T.line}; --muted: ${T.muted};
  --fill: ${T.fill}; --fill-soft: ${T.fillSoft};
  --accent-tint: rgba(0,113,227,.10); --accent-ring: rgba(0,113,227,.30);
  --r-s: ${T.radiusS}; --r-m: ${T.radiusM}; --r-l: ${T.radiusL}; --r-xl: ${T.radiusXL};
  --shadow-card: 0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.04);
  --shadow-raised: 0 1px 2px rgba(16,24,40,.10), 0 0 0 .5px rgba(16,24,40,.06);
  --shadow-pop: 0 18px 50px rgba(16,24,40,.22), 0 2px 8px rgba(16,24,40,.08);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", system-ui, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  -webkit-tap-highlight-color: transparent;
}
.pd .mono { font-family: ui-monospace, "SF Mono", "SFMono-Regular", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }

/* ───────────── 2. SHELL + NAV ───────────── */
.pd .topbar { align-items:center; margin-bottom:14px; }
.pd .brandbar { gap:15px; }
.pd .brandbar > div { padding-left:15px; }
.pd h1, .pd .brandbar h1 { text-transform:none; font-size:22px; font-weight:700; letter-spacing:-0.45px; color:var(--ink); }
.pd .sub, .pd .brandbar .sub { font-size:12.5px; letter-spacing:0; margin-top:1px; }
.pd .topright { gap:8px; }
.pd .whobar b { font-size:13.5px; font-weight:600; }
.pd .whobar em { text-transform:none; letter-spacing:0; font-size:11.5px; font-weight:500; }
.pd .iconbtn { padding:8px 10px; border-radius:var(--r-m); }
.pd .iconbtn .badge { border:2px solid var(--plaster); min-width:19px; height:19px; top:-7px; right:-7px; font-weight:700; }

.pd .mainnav { gap:2px; padding:5px; border-radius:var(--r-l); border-color:var(--line); box-shadow:var(--shadow-card); }
.pd .mainnav button { padding:8px 14px; border-radius:var(--r-m); font-size:13.5px; font-weight:550; letter-spacing:-0.1px; color:var(--muted); }
.pd .mainnav button:hover { background:var(--fill); color:var(--ink); }
.pd .mainnav button[data-on="1"] { background:var(--accent-tint); border-color:transparent; color:var(--accent-deep); font-weight:650; }
.pd .mainnav button[data-on="1"] svg { color:var(--tile); }
.pd .mainnav .navflip { font-size:12px; font-weight:500; }
@media (min-width: 981px) {
  .pd[data-nav="right"] .mainnav { padding:8px; border-radius:var(--r-xl); background:rgba(255,255,255,.78); -webkit-backdrop-filter:saturate(180%) blur(20px); backdrop-filter:saturate(180%) blur(20px); }
  .pd[data-nav="right"] .mainnav button { padding:9px 12px; }
}
.pd .phonenav-head { padding:16px 18px; }
.pd .phonenav-head h2 { font-size:20px; font-weight:700; letter-spacing:-0.3px; }
.pd .phonenav { padding:8px 10px 28px; gap:1px; }
.pd .phonenav button { padding:13px 14px; border-radius:var(--r-m); font-size:16px; font-weight:500; color:var(--ink); }
.pd .phonenav button svg { color:var(--tile); }
.pd .phonenav button:hover { background:var(--fill); }
.pd .phonenav button[data-on="1"] { background:var(--accent-tint); color:var(--accent-deep); font-weight:650; }
.pd .posdrawer-wrap { background:rgba(16,24,40,.32); -webkit-backdrop-filter:blur(6px); backdrop-filter:blur(6px); }
.pd .posdrawer { box-shadow:var(--shadow-pop); }

/* segmented controls (small tab strips) — grey track, white thumb */
.pd .tabs, .pd .seg { background:var(--fill); border:0; border-radius:var(--r-m); padding:2px; gap:2px; overflow:visible; }
.pd .tabs button, .pd .seg button { background:transparent; border:0; border-radius:var(--r-s); padding:6px 14px; font-size:13px; font-weight:550; color:var(--ink); }
.pd .tabs button[data-on="1"], .pd .seg button[data-on="1"] { background:#fff; color:var(--ink); font-weight:650; box-shadow:var(--shadow-raised); }

/* ───────────── 3. CORE CONTROLS ───────────── */
.pd .panel { border-radius:var(--r-l); border-color:var(--line); box-shadow:var(--shadow-card); }
.pd .pad { padding:16px; }
.pd .btn { font-size:13.5px; font-weight:600; letter-spacing:-0.1px; padding:8px 15px; border-radius:var(--r-m); }
.pd .btn:not(.ghost):not(.warn) { box-shadow:none; }
.pd .btn.ghost { border-color:var(--line); box-shadow:0 1px 1px rgba(16,24,40,.04); }
.pd .btn.ghost:hover { background:var(--fill-soft); border-color:#CBD2DC; box-shadow:0 1px 2px rgba(16,24,40,.08); }
.pd .btn.warn { border-color:#F3CFCF; }
.pd .btn.mini { font-size:12px; padding:5px 10px; border-radius:var(--r-s); font-weight:600; }
.pd .btn:hover { box-shadow:0 2px 6px rgba(16,24,40,.12); }
/* hover goes DARKER, not lighter — the lighter blue dropped white text to 3.7:1 */
.pd .btn:not(.ghost):not(.warn):not(:disabled):hover { background:var(--tile-lit); border-color:var(--tile-lit); }
.pd .btn:active { transform:scale(.98); }
.pd .btn:disabled, .pd .btn:disabled:hover, .pd .btn:disabled:active { opacity:.4; transform:none; box-shadow:none; }
@media (pointer: coarse) {
  .pd .btn:not(.mini), .pd .mainnav button, .pd .tabs button, .pd .seg button { min-height:41px; }
  .pd .btn.mini, .pd .commbtn { min-height:26px; }
  .pd .iconbtn { min-width:41px; justify-content:center; }
}

.pd label { font-size:12px; font-weight:600; letter-spacing:0; margin-bottom:5px; }
.pd input, .pd select, .pd textarea { border-radius:var(--r-m); border-color:var(--line); padding:9px 11px; font-size:13.5px; transition:border-color .12s ease, box-shadow .12s ease; }
.pd input::placeholder, .pd textarea::placeholder { color:#6E6E73; opacity:1; }
.pd input:focus, .pd select:focus, .pd textarea:focus { outline:none; border-color:var(--tile); box-shadow:0 0 0 3.5px var(--accent-ring); }
.pd button:focus-visible { outline:none; box-shadow:0 0 0 3.5px var(--accent-ring); }
.pd input[type="checkbox"], .pd input[type="radio"] { accent-color:var(--tile); box-shadow:none; }

.pd .overlay { background:rgba(16,24,40,.42); -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px); }
.pd .sheet { border-radius:var(--r-xl); box-shadow:var(--shadow-pop); animation:applesheet .22s cubic-bezier(.2,.8,.2,1); }
@keyframes applesheet { from { opacity:0; transform:translateY(10px) scale(.985); } }
@media (prefers-reduced-motion: reduce) { .pd .sheet { animation:none; } }

.pd .sechead, .pd details.rfold > summary { text-transform:none; letter-spacing:-0.1px; font-size:13px; font-weight:650; color:var(--muted); margin-top:18px; padding-bottom:7px; }
.pd .pill { border-radius:999px; padding:2px 9px; font-size:10.5px; font-weight:650; letter-spacing:.2px; }
.pd .chip { border-radius:999px; padding:3px 10px; font-weight:600; }
.pd .doc { border-radius:6px; padding:3px 8px; font-weight:600; letter-spacing:.1px; }
.pd .tostat { border-radius:999px; padding:2px 9px; letter-spacing:.3px; font-weight:700; }
.pd .empty { font-size:14px; padding:34px 18px; }

/* customer list (left column of the Customers screen) */
.pd .listhead { text-transform:none; letter-spacing:0; font-size:12px; font-weight:650; padding:9px 16px 7px; background:rgba(246,247,250,.9); -webkit-backdrop-filter:blur(12px); backdrop-filter:blur(12px); }
.pd .clientrow { padding:12px 16px; transition:background .1s ease; }
.pd .clientrow:hover { background:var(--fill-soft); }
.pd .clientrow[data-on="1"] { background:var(--accent-tint); box-shadow:none; }
.pd .cr-name { font-size:14.5px; font-weight:600; letter-spacing:-0.2px; }
.pd .cr-meta { font-size:12px; margin-top:3px; }

/* ───────────── 4. DENSE SCREEN: CUSTOMER PROFILE ───────────── */
.pd .detail-name { font-size:28px; font-weight:700; letter-spacing:-0.6px; line-height:1.14; }
.pd .gatecard { background:linear-gradient(160deg,#1C3F80 0%,#142C5C 100%); border-radius:16px; padding:16px 18px; margin-bottom:14px; box-shadow:0 6px 20px rgba(20,44,92,.18); }
.pd .gatecard .lbl { text-transform:none; letter-spacing:0; font-size:12px; font-weight:600; color:#A9D6F2; }
.pd .gatecode { font-size:26px; font-weight:650; letter-spacing:1px; }
.pd .gatenote { font-weight:600; font-size:13.5px; border-top-color:rgba(255,255,255,.14); padding-top:10px; }
.pd .meterwrap { gap:10px; }
.pd .meterwrap .mcell { border-radius:12px; background:rgba(255,255,255,.09); border-color:rgba(255,255,255,.12); padding:9px 13px 10px; }

.pd .glance { gap:8px; margin:12px 0 4px; }
.pd .gcell { border:0; border-radius:12px; background:var(--fill-soft); padding:10px 10px 9px; }
.pd .gcell .gv { font-family:inherit; font-variant-numeric:tabular-nums; font-size:16px; font-weight:650; letter-spacing:-0.2px; }
.pd .gcell .gv.txt { font-size:12.5px; font-weight:600; letter-spacing:0; }
.pd .gcell .gl { text-transform:none; letter-spacing:0; font-size:11.5px; font-weight:500; margin-top:2px; }

.pd .commtiles { gap:10px; }
.pd .commtile { border-radius:var(--r-l); padding:13px 8px 12px; gap:2px; }
.pd .commtile:hover { box-shadow:0 4px 14px rgba(16,24,40,.08); }
.pd .commtile:active { transform:scale(.98); }
.pd .commtile .ct-ic { border-radius:50%; background:var(--accent-tint); margin-bottom:5px; }
.pd .commtile b { font-size:13.5px; font-weight:600; }
.pd .commtile .ct-pref { font-size:11.5px; font-weight:650; letter-spacing:0; }

.pd .stackpanel { border:0; border-radius:var(--r-l); background:var(--fill-soft); padding:15px; margin-top:12px; }
.pd .stackpanel .ptitle { text-transform:none; letter-spacing:-0.1px; font-size:13px; font-weight:650; }
.pd .recpair { gap:10px; }
.pd .recrow { border-radius:12px; background:#fff; border-color:var(--line); padding:11px 12px; }
.pd .recrow .lbl2 { text-transform:none; letter-spacing:0; font-size:12px; font-weight:600; }
.pd .recrow .bigdate { font-family:inherit; font-variant-numeric:tabular-nums; font-size:18px; font-weight:650; letter-spacing:-0.3px; text-decoration:none; padding-top:3px; }
.pd .recrow .bigdate:hover { text-decoration:underline; text-underline-offset:3px; }
.pd .recrow .none2 { color:var(--muted); }

.pd .kv { font-size:14px; line-height:1.55; }
.pd .lrow { padding:10px 14px; font-size:13.5px; border-bottom-color:var(--line); }
.pd .lrow:nth-child(odd) { background:transparent; }
.pd .lrow b { font-weight:600; font-variant-numeric:tabular-nums; }
.pd .visit { border-radius:12px; padding:14px; border-color:var(--line); }
.pd .visit.latest { border-color:#BFDDF3; background:#F6FAFE; }
.pd .cardbox { border-radius:12px; }
.pd .cardface { border-radius:12px; }
.pd .flagbtn { border-radius:var(--r-m); font-weight:650; }
/* Apple HIG minimum text size (11pt on phones): lift the smallest profile labels. Colours untouched. */
.pd .ctag { font-size:11px; letter-spacing:.3px; font-weight:650; text-transform:none; }
.pd .cbadge { font-size:10.5px; letter-spacing:.3px; font-weight:700; }
.pd .srcchip { font-size:10.5px; letter-spacing:.3px; font-weight:650; border-radius:999px; padding:2px 9px; }
.pd .eqrow .kind { font-size:11px; letter-spacing:.2px; font-weight:650; text-transform:none; }
.pd .bf-scope { font-size:10.5px; }

/* ═════════════ PASS 2 (2026-09-20) — make the Apple feel obvious ═════════════
   Owner said pass 1 looked the same as live. This section is the visible shift:
   Mac-style sidebar in groups, large titles, neutral text + Apple blue, capsule
   buttons, borderless cards with more air, and no ALL-CAPS labels. */

/* 5a. No ALL CAPS where staff read. Every rule in the main sheet that forced
   capitals is switched off here by name (list generated from the sheet).
   Kept on purpose: the red safety tags (.bf-scope, .stoptag, STOP message). */
.pd .postile .pt-cat, .pd .poscheckout-total span, .pd .poscartline .pcl-bot label, .pd .tostat,
.pd .statussteps, .pd .pick .prole, .pd .newtag, .pd .whobar em, .pd .listhead, .pd .pill,
.pd .gatecard .lbl, .pd .sechead, .pd details.rfold > summary, .pd .gcell .gl, .pd .ctag, .pd .cbadge,
.pd .invgrid th, .pd .shot .tag, .pd .jobkind, .pd .pfsec, .pd .pftag, .pd .droptag, .pd .offtag, .pd .mhead,
.pd .stackpanel .ptitle, .pd .recrow .lbl2, .pd .trendcell .tl, .pd .srcchip, .pd .flagbanner .who,
.pd .flagpop .top, .pd .eqrow .kind, .pd .salecell .l, .pd .dl-label, .pd .salest th, .pd .lvl .lvlcap
  { text-transform:none; letter-spacing:0; }
/* …and the handful set inline in the markup */
.pd [style*="text-transform: uppercase"], .pd [style*="text-transform:uppercase"] { text-transform:none !important; letter-spacing:0 !important; }
/* sentence-case labels read smaller than caps did — keep them at a legible size */
.pd .jobkind, .pd .pfsec, .pd .pftag, .pd .droptag, .pd .offtag, .pd .mhead, .pd .trendcell .tl, .pd .salecell .l,
.pd .dl-label, .pd .lvl .lvlcap, .pd .shot .tag, .pd .pick .prole, .pd .newtag, .pd .statussteps, .pd .postile .pt-cat
  { font-size:11px; font-weight:600; }
.pd .invgrid th, .pd .salest th { font-size:12px; font-weight:600; }

/* 5b. Type scale — Apple text styles, scaled for a desktop CRM (before the 1.08 zoom) */
div.pd { font-size:14px; line-height:1.45; color:var(--ink); }
.pd h1, .pd .brandbar h1 { font-size:26px; font-weight:700; letter-spacing:-0.6px; color:var(--ink); }
.pd .sub, .pd .brandbar .sub { font-size:13px; color:var(--muted); }
.pd h2 { letter-spacing:-0.35px; }
.pd .detail-name { font-size:32px; font-weight:700; letter-spacing:-0.8px; line-height:1.1; }
.pd .sechead, .pd details.rfold > summary { font-size:17px; font-weight:650; letter-spacing:-0.3px; color:var(--ink); border-bottom:0; padding-bottom:2px; margin:30px 0 10px; }
.pd .sechead:first-child { margin-top:0; }
.pd .stackpanel .ptitle { font-size:15px; font-weight:650; letter-spacing:-0.2px; color:var(--ink); margin-bottom:12px; }
.pd .ctag { color:var(--muted); font-size:12px; font-weight:500; }
.pd .listhead { font-size:12.5px; color:var(--muted); }
.pd label { font-size:12.5px; color:var(--muted); font-weight:500; }

/* 5c. Surfaces — borderless white cards on grouped grey, more air */
.pd .panel { border:0; border-radius:var(--r-xl); box-shadow:0 0 0 .5px rgba(0,0,0,.06), 0 2px 10px rgba(0,0,0,.04); }
.pd .grid { gap:20px; }
.pd .stackpanel { background:var(--fill-soft); border-radius:var(--r-l); padding:18px; margin-top:14px; }
.pd .recrow, .pd .visit, .pd .cardbox, .pd .msgrow { border-color:transparent; box-shadow:0 0 0 .5px rgba(0,0,0,.08); background:#fff; }
.pd .visit.latest { background:#fff; box-shadow:0 0 0 1.5px var(--accent-ring); }
.pd .gcell { background:var(--fill-soft); border-radius:14px; padding:14px 10px 12px; }
.pd .gcell .gv { font-size:17px; font-weight:650; letter-spacing:-0.4px; }
.pd .gcell .gl { font-size:12px; margin-top:3px; color:var(--muted); }
.pd .glance { gap:10px; margin:16px 0 6px; }
.pd .gatecard { background:linear-gradient(160deg,#20407F 0%,#14284F 100%); border-radius:20px; padding:18px 20px; margin-bottom:18px; }
.pd .gaterow { gap:16px; }
.pd .meterwrap .mcell { min-width:96px; padding:8px 10px 9px; }
.pd .commtile { border-color:transparent; box-shadow:0 0 0 .5px rgba(0,0,0,.08); border-radius:var(--r-l); padding:16px 8px 14px; }
.pd .commtile .ct-ic { width:48px; height:48px; }
.pd .lrow { padding:12px 14px; font-size:14px; }
.pd .kv { font-size:14.5px; }
.pd .doc { background:#3A3A3C; border-radius:999px; padding:3px 10px; font-family:inherit; font-size:11.5px; font-weight:600; letter-spacing:0; }
.pd .doc.inv { background:var(--tile); }
.pd .careverdict { display:flex; flex-wrap:wrap; align-items:center; gap:6px 8px; margin-top:8px !important; }
.pd .careverdict .carenote { color:var(--muted); font-size:12.5px; }

/* 5d. Controls — capsule buttons, quieter secondary buttons */
/* 17px = half the height of a one-line button, so it reads as a capsule; tall two-line buttons stay rounded rectangles */
.pd .btn { border-radius:17px; padding:8px 17px; font-weight:590; }
.pd .commbtn { border-radius:999px; padding:5px 12px; border-color:transparent; box-shadow:0 0 0 .5px rgba(0,0,0,.12); color:var(--accent-deep); }
.pd .btn.mini { border-radius:999px; padding:5px 12px; }
.pd .btn svg, .pd .commbtn svg { flex:none; }
.pd .btn.ghost { background:#fff; border-color:transparent; color:var(--accent-deep); box-shadow:0 0 0 .5px rgba(0,0,0,.10), 0 1px 2px rgba(0,0,0,.05); }
.pd .btn.ghost:hover { background:var(--fill-soft); border-color:transparent; }
.pd .btn.warn { border-color:transparent; box-shadow:0 0 0 .5px rgba(220,21,21,.35); }
.pd .iconbtn:not(.phonemenu-btn) { width:38px; height:38px; padding:0; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; color:var(--ink); }
.pd input, .pd select, .pd textarea { border-color:#D2D2D7; color:var(--ink); }
.pd .switch { border-color:#D2D2D7; color:var(--ink); }
.pd .flagbtn, .pd .flagchip { border-radius:999px; }
.pd .tabs, .pd .seg { border-radius:999px; padding:3px; }
.pd .tabs button, .pd .seg button { border-radius:999px; padding:6px 16px; }

/* 5e. Shell — Mac / iPad style sidebar, grouped, quiet secondary items */
.pd .topbar { margin-bottom:22px; }
.pd .brandbar > div { border-left:0; padding-left:4px; }
.pd .navgroup { font-size:12px; font-weight:600; color:var(--muted); padding:16px 12px 6px; letter-spacing:0; flex:none; }
.pd .navgroup:first-child { padding-top:4px; }
.pd .phonenav .navgroup { font-size:13px; padding:18px 14px 6px; }
.pd .mainnav button { font-weight:500; color:var(--ink); }
.pd .mainnav button svg { color:var(--tile); flex:none; }
.pd .mainnav button:not([data-g="daily"]) svg, .pd .phonenav button:not([data-g="daily"]) svg { color:#8E8E93; }
.pd .mainnav button[data-on="1"] svg, .pd .phonenav button[data-on="1"] svg { color:var(--tile); }
.pd .mainnav .navflip { margin-top:auto; margin-left:0; color:var(--muted); font-size:12px; opacity:1; }
@media (min-width: 981px) {
  .pd[data-shell="side"] { padding:26px 32px 40px 292px; }
  .pd[data-shell="side"][data-nav="right"] { padding:26px 292px 40px 32px; }
  .pd[data-shell="side"] .mainnav, .pd[data-shell="side"][data-nav="right"] .mainnav {
    position:fixed; top:0; bottom:0; left:0; right:auto; width:260px; margin:0; padding:18px 12px 14px; gap:1px;
    flex-direction:column; flex-wrap:nowrap; align-items:stretch; overflow-y:auto; z-index:60;
    border:0; border-right:.5px solid rgba(0,0,0,.12); border-radius:0; box-shadow:none;
    background:rgba(239,239,244,.86); -webkit-backdrop-filter:saturate(180%) blur(24px); backdrop-filter:saturate(180%) blur(24px); }
  .pd[data-shell="side"][data-nav="right"] .mainnav { left:auto; right:0; border-right:0; border-left:.5px solid rgba(0,0,0,.12); }
  .pd[data-shell="side"] .mainnav button { justify-content:flex-start; padding:8px 12px; border-radius:9px; font-size:14px; gap:10px; margin-left:0 !important; white-space:normal; text-align:left; }
  .pd[data-shell="side"] .mainnav button[data-on="1"] { background:rgba(0,113,227,.12); }
  .pd[data-shell="side"] .grid:not([data-solo="1"]) { grid-template-columns:300px minmax(0,1fr); }
}
/* tablets: one thin scrolling row instead of two wrapped rows; headings hidden */
@media (min-width: 821px) and (max-width: 980px) {
  .pd .mainnav { flex-wrap:nowrap; overflow-x:auto; scrollbar-width:none; -webkit-mask-image:linear-gradient(90deg,#000 90%,transparent); mask-image:linear-gradient(90deg,#000 90%,transparent); }
  .pd .mainnav::-webkit-scrollbar { display:none; }
  .pd .mainnav .navgroup { display:none; }
  .pd .mainnav .navflip { display:none; }
  .pd .mainnav button { flex:none; }
}
@media (max-width: 820px) { .pd .grid { grid-template-columns:minmax(0,1fr); } .pd .sechead, .pd details.rfold > summary { font-size:16px; margin-top:24px; } .pd .detail-name { font-size:27px; } .pd .gcell .gv { font-size:17px; } .pd .gatecard { padding:15px 16px; border-radius:16px; } .pd .stackpanel { padding:14px; } }

/* Phones: keep the tighter original paddings so nothing gets wider than it was. */
@media (max-width: 820px) {
  .pd .pad { padding:14px; }
  .pd .lrow { padding:9px 12px; }
  .pd input, .pd select, .pd textarea { padding:8px 10px; }
  .pd .stackpanel { padding:13px; }
  .pd .gatecard { padding:13px 15px; }
  .pd .meterwrap { gap:20px; }
  .pd .meterwrap .mcell { padding:8px 12px 9px; }
}

/* Apple HIG — Reduce Motion: swap movement for fades, never scale or slide. */
@media (prefers-reduced-motion: reduce) {
  .pd .btn, .pd .commtile, .pd .mainnav button, .pd input, .pd select, .pd textarea { transition:none; }
  .pd .btn:active, .pd .commtile:active { transform:none; }
  .pd .posdrawer-wrap { animation:none; }
}
/* Apple HIG — Increase Contrast: darker secondary text, firmer lines, deeper accent. */
@media (prefers-contrast: more) {
  div.pd { --muted:#3A3A3C; --line:#8E8E93; --tile:#004F9F; --tile-lit:#003F80; --accent-deep:#003F80; --accent-tint:rgba(0,79,159,.16); }
  .pd .btn.ghost, .pd .panel, .pd .commtile, .pd .recrow, .pd .visit { border-color:#7C8797; }
  .pd .gcell, .pd .stackpanel { box-shadow:inset 0 0 0 1px #9AA5B4; }
  .pd .overlay, .pd .posdrawer-wrap { -webkit-backdrop-filter:none; backdrop-filter:none; background:rgba(16,24,40,.6); }
}
`;
